import { useState } from 'react';
import ArrowNarrowDown from 'svg/ArrowNarrowDown';
import ArrowRight from 'svg/ArrowRight';
import Plus from 'svg/Plus';
import { Button, ButtonText } from 'components/Button';
import { GameFormat } from 'components/Find/gameFormat';
import type { FindGamePlayer } from 'components/Find/types';
import classNames from 'styles/utils/classNames';
import OpenSeatsTile from './OpenSeatsTile';
import PlayerSlot from './PlayerSlot';
import UnlimitedSeat from './UnlimitedSeat';

/** Open Play shows the host plus this many seats; anything beyond folds into a count tile. */
const OPEN_PLAY_VISIBLE_SEATS = 8;
/** The details modal's roster from lg up: six seats a row, two rows until expanded. */
const OPEN_PLAY_DESKTOP_COLUMNS = 6;
const OPEN_PLAY_DESKTOP_VISIBLE_SEATS = OPEN_PLAY_DESKTOP_COLUMNS * 2;

interface Props {
  teams: FindGamePlayer[][];
  /** Seats per side; for Open Play, the roster's total capacity. */
  playersPerTeam: number;
  /** Open Play lays the roster out as host + a 4-up grid instead of two sides. */
  format?: GameFormat;
  /** Open Play with no participant limit: the grid ends in an ∞ seat. */
  isUnlimited?: boolean;
  /** When false, drops the card's bordered/shadowed box — used in the details modal. */
  bordered?: boolean;
  /** Wraps the seats into a 2-up grid sized for the mobile feed's 120px tile. */
  compact?: boolean;
  /** The details modal's roomier roster from lg up (Figma "Modal M"). */
  isLargeAtDesktop?: boolean;
  /** Figma "Game card Desktop": each side stacks into its own column from lg. */
  isStackedAtDesktop?: boolean;
  /** Host-only: puts an "Invite" pill under every open singles/doubles seat. */
  onInviteOpenSeat?: () => void;
  /**
   * Host-only, Open Play: renders the "+ Invite players" / "View all participants"
   * row under the roster. The roster's grid is too tight for per-seat invites.
   */
  onInvitePlayers?: () => void;
}

/** Pads a side out to `playersPerTeam`, so unfilled seats render as "open". */
const withOpenSlots = (team: FindGamePlayer[], playersPerTeam: number) =>
  Array.from({ length: playersPerTeam }, (_, index) => team[index] ?? null);

export default function GamePlayers({
  teams,
  playersPerTeam,
  format,
  isUnlimited = false,
  bordered = true,
  compact = false,
  isLargeAtDesktop = false,
  isStackedAtDesktop = false,
  onInviteOpenSeat,
  onInvitePlayers,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Compact drops the per-team split (there is no room for a divider at 120px)
  // and lays every seat out in one 2-up grid.
  const isOpenPlay = format === GameFormat.OpenPlay;

  if (compact) {
    // Open Play's capacity can be large; the 2x2 tile shows the first four seats.
    const seats = isOpenPlay
      ? withOpenSlots(teams.flat(), isUnlimited ? 3 : Math.min(playersPerTeam, 4))
      : teams.flatMap((team) => withOpenSlots(team, playersPerTeam));

    return (
      <div
        className={classNames(
          'grid h-full w-full grid-cols-2 content-center gap-ds-xs overflow-hidden p-ds-xs',
          bordered &&
            'rounded-lg border border-color-border-input-lightmode bg-color-bg-lightmode-primary dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary',
        )}
      >
        {/* Singles/doubles cap the roster at four, which is exactly the 2x2 tile. */}
        {seats.map((player, index) => (
          <PlayerSlot key={player?.id ?? `open-${index}`} player={player} compact />
        ))}
        {isOpenPlay && isUnlimited && <UnlimitedSeat compact />}
      </div>
    );
  }

  if (isOpenPlay) {
    const [host, ...others] = teams.flat();
    const capacity = Math.max(playersPerTeam, 1);
    const seatCount = capacity - 1;
    const openCount = Math.max(seatCount - others.length, 0);

    // A roster too long for the grid gives its last cell to a count tile; unlimited
    // always ends in the ∞ seat.
    const seatsWithin = (cells: number) =>
      isUnlimited || seatCount > cells ? cells - 1 : seatCount;
    const hasCountTile = (shown: number) => !isUnlimited && shown < seatCount;

    const cells = isUnlimited ? others.length + 1 : seatCount;
    const canExpand = isLargeAtDesktop && cells > OPEN_PLAY_DESKTOP_VISIBLE_SEATS;
    // The host's action row brings the toggle to mobile, where the grid otherwise
    // always ends in the count tile.
    const canExpandMobile = !isUnlimited && cells > OPEN_PLAY_VISIBLE_SEATS;
    const mobileSeats =
      canExpandMobile && isExpanded ? seatCount : seatsWithin(OPEN_PLAY_VISIBLE_SEATS);
    // Expanded, an unlimited roster pads with open seats so ∞ still closes a full row.
    const desktopSeats = !isLargeAtDesktop
      ? mobileSeats
      : canExpand && isExpanded
      ? isUnlimited
        ? Math.ceil(cells / OPEN_PLAY_DESKTOP_COLUMNS) * OPEN_PLAY_DESKTOP_COLUMNS - 1
        : seatCount
      : seatsWithin(OPEN_PLAY_DESKTOP_VISIBLE_SEATS);
    const seats = withOpenSlots(others, Math.max(mobileSeats, desktopSeats));

    return (
      <>
        <div
          className={classNames(
            'flex items-start gap-ds-sm',
            isLargeAtDesktop && 'lg:gap-ds-lg',
            bordered
              ? 'rounded-xl border border-color-border-input-lightmode bg-color-bg-lightmode-primary p-ds-sm shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)] dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary'
              : 'py-ds-sm',
          )}
        >
          <div className={classNames('w-20 shrink-0', isLargeAtDesktop && 'lg:w-[7.5rem]')}>
            <PlayerSlot player={host ?? null} isLargeAtDesktop={isLargeAtDesktop} />
          </div>
          <div className="w-px self-stretch bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />
          <div
            className={classNames(
              'grid min-w-0 flex-1 grid-cols-4 gap-ds-sm',
              isLargeAtDesktop && 'lg:grid-cols-6 lg:gap-x-ds-lg lg:py-ds-xs',
            )}
          >
            {seats.map((player, index) => (
              <PlayerSlot
                key={player?.id ?? `open-${index}`}
                player={player}
                compact
                isLargeAtDesktop={isLargeAtDesktop}
                className={classNames(
                  index >= mobileSeats && 'max-lg:hidden',
                  index >= desktopSeats && 'lg:hidden',
                )}
              />
            ))}
            {isUnlimited && <UnlimitedSeat />}
            {(hasCountTile(mobileSeats) || hasCountTile(desktopSeats)) && (
              <OpenSeatsTile
                openCount={openCount}
                totalCount={capacity}
                className={classNames(
                  !hasCountTile(mobileSeats) && 'max-lg:hidden',
                  !hasCountTile(desktopSeats) && 'lg:hidden',
                )}
              />
            )}
          </div>
        </div>

        {(onInvitePlayers || canExpandMobile) && (
          <div className="mt-ds-md flex items-center justify-end gap-ds-md">
            {onInvitePlayers && (
              <Button
                variant="primary"
                size="xs"
                isInline
                iconLeft={<Plus className="size-3" />}
                onClick={onInvitePlayers}
                className="mr-auto"
              >
                Invite players
              </Button>
            )}

            {(canExpandMobile || canExpand) && (
              <ButtonText
                size="sm"
                onClick={() => setIsExpanded((expanded) => !expanded)}
                aria-expanded={isExpanded}
                className="inline-flex items-center gap-ds-xs text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
              >
                {isExpanded ? 'View fewer participants' : 'View all participants'}
                <ArrowNarrowDown
                  className={classNames('size-3.5 shrink-0', isExpanded && 'rotate-180')}
                />
              </ButtonText>
            )}
          </div>
        )}

        {canExpand && !onInvitePlayers && !canExpandMobile && (
          <div className="mt-ds-sm flex justify-end max-lg:hidden">
            <ButtonText
              size="md"
              onClick={() => setIsExpanded((expanded) => !expanded)}
              aria-expanded={isExpanded}
              className="inline-flex items-center gap-ds-xs text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
            >
              {isExpanded ? 'View less' : 'View more'}
              <ArrowRight
                className={classNames('h-4 w-4 shrink-0', isExpanded ? '-rotate-90' : 'rotate-90')}
              />
            </ButtonText>
          </div>
        )}
      </>
    );
  }

  return (
    <div
      className={classNames(
        'flex items-start gap-ds-sm',
        bordered
          ? 'rounded-xl border border-color-border-input-lightmode bg-color-bg-lightmode-primary p-ds-sm shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)] dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary'
          : 'py-ds-sm',
        // The desktop card owns the box; this becomes its left panel.
        isStackedAtDesktop &&
          'lg:h-full lg:items-stretch lg:rounded-none lg:border-0 lg:p-0 lg:shadow-none',
      )}
    >
      {teams.map((team, teamIndex) => (
        <div
          key={teamIndex}
          className={classNames(
            'flex flex-1 items-start gap-ds-sm border-color-border-input-lightmode dark:border-color-border-input-darkmode [&:not(:first-child)]:border-l [&:not(:first-child)]:pl-ds-sm',
            isStackedAtDesktop && 'lg:flex-col lg:justify-center lg:self-stretch',
          )}
        >
          {withOpenSlots(team, playersPerTeam).map((player, slotIndex) => (
            <PlayerSlot
              key={player?.id ?? `open-${teamIndex}-${slotIndex}`}
              player={player}
              isLargeAtDesktop={isLargeAtDesktop}
              onInvite={onInviteOpenSeat}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
